import React from "react";
import API from "../../services/api";

// Every loader funnels failures through this so the operator sees the
// server's reason (FastAPI puts it in `detail`) instead of a panel that
// silently never fills. A 422 carries `detail` as a list - flatten it.
export const errMsg = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (detail) {
    try { return JSON.stringify(detail); } catch { /* fall through */ }
  }
  return err?.message || "Request failed";
};

// Plain <a href> links carry no Authorization header, so every download
// 401'd the moment auth went live. Fetch through the axios client (which
// attaches the token) and hand the browser a blob URL instead.
export async function downloadWithAuth(path, filename) {
  let res;
  try {
    res = await API.get(path, { responseType: "blob" });
  } catch (err) {
    // an error body arrives as a Blob - unwrap it so errMsg can read `detail`
    const body = err?.response?.data;
    if (body && typeof body.text === "function") {
      try { err.response.data = JSON.parse(await body.text()); } catch { /* keep as-is */ }
    }
    throw err;
  }
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // let the browser start the download before the URL goes away
  setTimeout(() => window.URL.revokeObjectURL(url), 1500);
}

export const nzToday = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });

export const addDay = (d, n) => {
  try {
    const dt = new Date(String(d).slice(0, 10) + "T00:00:00");
    if (Number.isNaN(dt.getTime())) return d;
    dt.setDate(dt.getDate() + n);
    return dt.toLocaleDateString("en-CA");   // YYYY-MM-DD
  } catch { return d; }
};

// The day a run is DRIVEN. The backend resolves it (persisted delivery_date,
// else the sheet's own date, else received + 1); the +1 fallback keeps older
// API builds from filing runs under the wrong day.
export const deliveryOf = (r) => {
  if (!r) return "";
  return String(r.delivery_date || addDay(String(r.date || ""), 1) || "").slice(0, 10);
};

export const prettyDate = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(String(d).slice(0, 10) + "T00:00:00");
    if (Number.isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("en-NZ", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
};

// delivered_at / picked_up_at are utcnow().isoformat() with no zone suffix;
// left alone the browser would read them as local and show a POD time hours
// out. Pin them to UTC, then render in NZ time.
export const fmtTime = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  const withTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
  try {
    const dt = new Date(withTz);
    if (Number.isNaN(dt.getTime())) return s.slice(11, 16);
    return dt.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit", timeZone: "Pacific/Auckland" });
  } catch { return s.slice(11, 16); }
};

// A job's own delivery day as YYYY-MM-DD. preferred_delivery_date is free
// text from the parser (25/07/2026, 25/07/26, 2026-07-25); null when unknown.
export const jobDeliveryDate = (job) => {
  const s = String(job?.delivery_date || job?.preferred_delivery_date || "").trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/);
  if (m) {
    const yy = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${yy}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  }
  return null;
};

export const RUN_STATUSES = ["draft", "planned", "in_progress", "completed", "cancelled"];

// Assign-to-run choices are about WHEN the truck goes: upcoming runs first
// (soonest at the top, which is where tomorrow's run lives), then history
// newest-first so an old sheet is still reachable.
export const sortRunsForPicker = (runs, today = nzToday()) => {
  const byName = (a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id));
  const up = [];
  const past = [];
  (runs || []).forEach((r) => (deliveryOf(r) >= today ? up : past).push(r));
  up.sort((a, b) => deliveryOf(a).localeCompare(deliveryOf(b)) || byName(a, b));
  past.sort((a, b) => deliveryOf(b).localeCompare(deliveryOf(a)) || byName(a, b));
  return [...up, ...past];
};

export const runLabel = (r) => {
  const bits = [r.name || `Run #${r.id}`, deliveryOf(r) || "no date"];
  if (r.driver_name) bits.push(r.driver_name);
  return bits.join(" · ");
};

// Per-stop exception flags -> what the operator reads on the sheet. Two
// parser outcomes collapse into one "needs info" label; colour still tells
// them apart.
export const EXCEPTION_META = {
  no_address: { label: "no address", cls: "text-red-300 border-red-500/40 bg-red-500/10" },
  no_phone: { label: "no phone", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  no_products: { label: "no items", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  low_confidence: { label: "needs info", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  unidentified: { label: "needs info", cls: "text-red-300 border-red-500/40 bg-red-500/10" },
  no_coordinates: { label: "no map pin", cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
};

// Until the API build that emits `exceptions` is live, derive the same flags
// from the fields the row already carries so the badges never just vanish.
export const exceptionsOf = (job) => {
  if (!job) return [];
  if (Array.isArray(job.exceptions)) return job.exceptions;
  const out = [];
  if (!(job.address_line || job.destination_address)) out.push("no_address");
  if (!job.recipient_phone) out.push("no_phone");
  if (!(Array.isArray(job.products) && job.products.length)) out.push("no_products");
  if (job.parse_status === "low_confidence") out.push("low_confidence");
  if (job.parse_status === "unidentified") out.push("unidentified");
  return out;
};

export function ExceptionBadges({ codes }) {
  const seen = new Set();
  const chips = (codes || []).map((c) => EXCEPTION_META[c] || { label: String(c).replace(/_/g, " "), cls: "text-slate-300 border-slate-600 bg-slate-700/40" })
    .filter((m) => (seen.has(m.label) ? false : seen.add(m.label)));
  if (!chips.length) return <span className="text-slate-600">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((m) => (
        <span key={m.label} className={`inline-block px-1.5 py-0.5 rounded-full border text-[8px] font-semibold uppercase tracking-wide whitespace-nowrap ${m.cls}`}>
          {m.label}
        </span>
      ))}
    </div>
  );
}

// Shared parse-status chip (matches the dashboard)
export const parseChip = (status) => {
  const map = {
    ok: { label: "OK", cls: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" },
    low_confidence: { label: "Low conf", cls: "text-amber-400 border-amber-500/30 bg-amber-500/10" },
    unidentified: { label: "Unidentified", cls: "text-red-400 border-red-500/30 bg-red-500/10" },
  };
  const s = map[status] || null;
  if (!s) return <span className="text-slate-600">—</span>;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-full border text-[8px] font-semibold uppercase tracking-wide whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
};

export const fmtProducts = (items) => {
  if (!items || !items.length) return "—";
  return items.map((i) => {
    const name = i.product?.code || i.sku || i.product?.description || i.raw_line || "Item";
    return `${name} ×${i.quantity || 1}`;
  }).join(", ");
};
